/**
 * Tarjeta de la librería de UI (Tailwind, estilos del proyecto).
 *
 * Conjunto presentacional de piezas componibles: `Card` (contenedor),
 * `CardHeader`, `CardTitle` y `CardContent`. Sin lógica de negocio. El título
 * es un `<h2>` por defecto para dar estructura accesible al contenido; quien lo
 * use puede cambiar el nivel con `as` si la jerarquía de la página lo requiere.
 */
import {
  forwardRef,
  type ElementType,
  type HTMLAttributes,
} from "react";

import { cn } from "./cn";

export type CardProps = HTMLAttributes<HTMLDivElement>;

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, ...props },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border border-slate-200 bg-white shadow-sm",
        className,
      )}
      {...props}
    />
  );
});

export const CardHeader = forwardRef<HTMLDivElement, CardProps>(
  function CardHeader({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn("border-b border-slate-200 px-5 py-4", className)}
        {...props}
      />
    );
  },
);

export interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  /** Nivel del encabezado (default `h2`) para encajar en la jerarquía. */
  as?: ElementType;
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  function CardTitle({ className, as, ...props }, ref) {
    const Comp = as ?? "h2";
    return (
      <Comp
        ref={ref}
        className={cn("text-lg font-semibold text-slate-900", className)}
        {...props}
      />
    );
  },
);

export const CardContent = forwardRef<HTMLDivElement, CardProps>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn("px-5 py-4", className)} {...props} />;
  },
);
